'use client';

import { Session } from 'next-auth';
import { redirect, useRouter } from 'next/navigation';
import { useEffect } from 'react';

import CartService from '@bytel/magicart';

import { getMobilePlans } from '@services/products';
import { getSessionVariable } from '@services/storage';

import { QUOTE_TYPES } from '@app-types/cart';
import { MobilePlanType } from '@app-types/plan';
import { ProductType } from '@app-types/product';
import { GALIUM_DATA_STORAGE_KEY, GalliumDataStoreApi, GalliumType } from '@app-types/renewal';
import { UserType } from '@app-types/user';

import { getCart } from '@helpers/cart';
import { paths } from '@helpers/path';

import { PlanTypeId } from '@constants/plan';

const addRenewalInfoToPlans = (plans: MobilePlanType[], allPlansGallium: GalliumDataStoreApi) => {
    return plans.map((plan) => {
        const isPlanOwned = allPlansGallium?.opSource.idOffre === plan.gencode;
        const newPlan = allPlansGallium?.simulation?.find((offer) => offer.idOffre === plan.gencode) || {};
        const source = isPlanOwned ? allPlansGallium.opSource : newPlan;

        const renewalDiscounts = source.remiseAvantage;
        const hasRenewalDiscounts = Math.abs(source.tarifMoinsRemise?.montantRemiseTTC || 0) > 0;

        const position = isPlanOwned
            ? 0
            : allPlansGallium?.simulation.map((offer) => offer.idOffre).indexOf(plan.gencode) + 1;

        return {
            ...plan,
            isOwned: isPlanOwned,
            position: position,
            renewalDiscounts: renewalDiscounts,
            hasRenewalDiscounts: hasRenewalDiscounts,
        };
    });
};

export function useInitPhoneInCartRenewal(
    setPlansRenewalList: (plans: MobilePlanType[]) => void,
    session?: Session,
    contractId?: string,
    isPro = false,
) {
    const router = useRouter();

    useEffect(() => {
        const user = session?.user as UserType;
        if (!user || !contractId) {
            return;
        }
        const plansInStorage: string | null = getSessionVariable(GALIUM_DATA_STORAGE_KEY);
        if (!plansInStorage) {
            router.push(paths.phoneWall);
            return;
        }
        const plansInStorageParsed: GalliumType = JSON.parse(plansInStorage);
        const allPlans = Object.values(plansInStorageParsed)[0];

        if (!allPlans) {
            router.push(paths.phoneWall);
            return;
        }

        getCart(user, contractId, isPro)
            .then(async (cart: CartService) => {
                const phone = cart.cart.quotes
                    ?.find((quote) => quote.context.id === QUOTE_TYPES.RENEWAL)
                    ?.products.find((product) => product.data.type_id === ProductType.PHONE_SIMPLE);

                if (!phone) {
                    router.push(paths.phoneWall);
                }
                const plansGencodes = [
                    allPlans?.opSource.idOffre,
                    ...[allPlans?.simulation.map((offer) => offer.idOffre)],
                ].join(',');

                const apiPlans = await getMobilePlans(
                    phone?.gencode,
                    true,
                    PlanTypeId.PREMIUM,
                    contractId,
                    plansGencodes,
                    session,
                    false,
                    false,
                );

                let plans = apiPlans;

                const ownedPlan = apiPlans.find((plan) => plan.gencode === allPlans.opSource.idOffre)!;

                if (ownedPlan.isExcluPro) {
                    plans = [ownedPlan];
                }

                setPlansRenewalList(addRenewalInfoToPlans(plans, allPlans)?.sort((a, b) => a.position - b.position));
            })
            .catch((error) => {
                console.error('Error while fetching plans', error);
                redirect(paths.phoneWall);
            });
    }, [contractId, session?.user?.sub]);
}
